/* ══════════════════════════════════════════════════════════════════
   Der Kalendertag des Betriebs — deutsche Zeit, nicht UTC.

   Prüfrunde Runde 8, **N8-02 (rot, 05.10.2026)**: Die API liefert
   Zeitpunkte in UTC (`2026-10-04T23:17:22.745Z`). Die Auftragsakte schnitt
   daraus mit `/^(\d{4})-(\d{2})-(\d{2})/` den Tag ab — den **UTC-Tag**. Ein
   Auftrag von 01:17 Uhr deutscher Zeit stand in der Akte am 04.10., auf
   seinen Papieren und im Abrechnungslauf am 05.10.

   **Hier steht die eine Lesart für die Oberfläche**, wie `tagImBetrieb` in
   der API (`apps/api/src/mitte/eingabe.ts`):

   - Ein **reiner Tag** (`JJJJ-MM-TT`) bleibt, was er ist — er hat keine
     Zeitzone.
   - Ein **Zeitpunkt** (ISO mit `Z`/Versatz oder Postgres-Text
     `2026-10-05 07:00:00+00`) zählt mit seinem Tag und seiner Uhrzeit in
     **Europe/Berlin** — auch in einem Browser, der woanders steht.

   Wer einen Tag oder eine Uhrzeit aus einem Zeitpunkt zeigt, nimmt sie von
   hier. `apps/web/test/kalendertag-web.test.ts` lehnt neue UTC-Tage im
   Web-Code ab (`toISOString().slice(0, 10)`, `getUTC…`, den ISO-Schnitt
   außerhalb dieser Datei, `toLocale…String` ohne `timeZone`).

   Ohne Abhängigkeiten, damit es überall und in jedem Test ladbar ist.
   ══════════════════════════════════════════════════════════════════ */

export const ZEITZONE = "Europe/Berlin";

const NUR_TAG = /^(\d{4})-(\d{2})-(\d{2})$/;

/** Die Uhr in Berlin, in Teilen. `en-CA` und `h23` sind Zahlenordnung, kein sichtbarer Text. */
const WANDUHR = new Intl.DateTimeFormat("en-CA", {
  timeZone: ZEITZONE,
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
  hour: "2-digit",
  minute: "2-digit",
  second: "2-digit",
  hourCycle: "h23",
});

const zwei = (n: number): string => String(n).padStart(2, "0");

/** `2026-10-05 07:00:00+00` (Postgres) und ISO gleichermaßen. */
export function zeitpunktLesen(text: string): Date {
  return new Date(/^\d{4}-\d\d-\d\d \d/.test(text) ? text.replace(" ", "T").replace(/([+-]\d\d)$/, "$1:00") : text);
}

/** Tag und Uhrzeit an der Wand in Berlin — oder `null`, wenn es kein lesbarer Wert ist. */
export type Wanduhr = { jahr: number; monat: number; tag: number; stunde: number; minute: number; sekunde: number };

export function wandImBetrieb(wert: unknown): Wanduhr | null {
  if (wert === null || wert === undefined || wert === "") return null;
  if (typeof wert === "string") {
    const t = NUR_TAG.exec(wert);
    if (t) return { jahr: Number(t[1]), monat: Number(t[2]), tag: Number(t[3]), stunde: 0, minute: 0, sekunde: 0 };
  }
  const d = wert instanceof Date ? wert : typeof wert === "string" ? zeitpunktLesen(wert) : null;
  if (!d || Number.isNaN(d.getTime())) return null;
  const teile: Record<string, number> = {};
  for (const teil of WANDUHR.formatToParts(d)) {
    if (teil.type !== "literal") teile[teil.type] = Number(teil.value);
  }
  return {
    jahr: teile["year"] ?? 0,
    monat: teile["month"] ?? 0,
    tag: teile["day"] ?? 0,
    stunde: (teile["hour"] ?? 0) % 24,
    minute: teile["minute"] ?? 0,
    sekunde: teile["second"] ?? 0,
  };
}

/** Der Kalendertag (`JJJJ-MM-TT`) eines Tages oder Zeitpunkts in Deutschland — `""`, wenn unlesbar. */
export function tagImBetrieb(wert: unknown): string {
  const w = wandImBetrieb(wert);
  return w ? `${w.jahr}-${zwei(w.monat)}-${zwei(w.tag)}` : "";
}

/** Heute, im Betrieb gezählt. Um 00:30 deutscher Zeit ist es hier schon morgen, in UTC noch gestern. */
export function heuteImBetrieb(jetzt: Date = new Date()): string {
  return tagImBetrieb(jetzt);
}

/** `JJJJ-MM-TT` plus `n` Kalendertage — ohne Zeitzone, ohne Sommerzeit. */
export function tagPlus(tag: string, n: number): string {
  const [j, m, t] = tag.split("-").map(Number);
  const d = new Date(Date.UTC(j ?? 1970, (m ?? 1) - 1, (t ?? 1) + n));
  return `${d.getUTCFullYear()}-${zwei(d.getUTCMonth() + 1)}-${zwei(d.getUTCDate())}`;
}

/**
 * `JJJJ-MM-TT` plus `n` Monate — dieselbe Regel wie Postgres
 * (`date + n months`): Der Tag bleibt, außer der Zielmonat ist kürzer; dann
 * wird es sein letzter. 31.01. plus ein Monat ist der 28.02.
 */
export function monatePlus(tag: string, n: number): string {
  const [j, m, t] = tag.split("-").map(Number);
  const ziel = new Date(Date.UTC(j ?? 1970, (m ?? 1) - 1 + n, 1));
  const letzter = new Date(Date.UTC(ziel.getUTCFullYear(), ziel.getUTCMonth() + 1, 0)).getUTCDate();
  return `${ziel.getUTCFullYear()}-${zwei(ziel.getUTCMonth() + 1)}-${zwei(Math.min(t ?? 1, letzter))}`;
}

/** Kalendertage von `von` bis `bis` (beide `JJJJ-MM-TT`), `bis` später = positiv. */
export function tageZwischen(von: string, bis: string): number {
  const ms = (tag: string) => {
    const [j, m, t] = tag.split("-").map(Number);
    return Date.UTC(j ?? 1970, (m ?? 1) - 1, t ?? 1);
  };
  return Math.round((ms(bis) - ms(von)) / 86_400_000);
}

/** Wie viele Kalendertage von heute bis zum Tag dieses Werts — negativ heißt vorbei. `null`, wenn unlesbar. */
export function tageBis(wert: unknown, jetzt: Date = new Date()): number | null {
  const tag = tagImBetrieb(wert);
  return tag ? tageZwischen(heuteImBetrieb(jetzt), tag) : null;
}

/** Der Wochentag eines Kalendertags, Montag = 0 … Sonntag = 6. */
export function wochentagVon(tag: string): number {
  const [j, m, t] = tag.split("-").map(Number);
  return (new Date(Date.UTC(j ?? 1970, (m ?? 1) - 1, t ?? 1)).getUTCDay() + 6) % 7;
}

/** `05.10.` — das Jahr steht im Kopf, nicht in der Zeile. `""`, wenn unlesbar. */
export function tagKurz(wert: unknown): string {
  const w = wandImBetrieb(wert);
  return w ? `${zwei(w.tag)}.${zwei(w.monat)}.` : "";
}

/** `05.10.2026`. `""`, wenn unlesbar. */
export function tagLang(wert: unknown): string {
  const w = wandImBetrieb(wert);
  return w ? `${zwei(w.tag)}.${zwei(w.monat)}.${w.jahr}` : "";
}

/** `07:30` in deutscher Zeit. Ein reiner Tag hat keine Uhrzeit (`""`). */
export function uhrImBetrieb(wert: unknown): string {
  if (typeof wert === "string" && NUR_TAG.test(wert)) return "";
  const w = wandImBetrieb(wert);
  return w ? `${zwei(w.stunde)}:${zwei(w.minute)}` : "";
}

/** `05.10. 07:30` — Tag und Uhrzeit in deutscher Zeit. */
export function tagUndUhr(wert: unknown): string {
  const tag = tagKurz(wert);
  const uhr = uhrImBetrieb(wert);
  return uhr ? `${tag} ${uhr}` : tag;
}

/** `05.10.2026, 07:30` — Datum mit Jahr und Uhrzeit, für Protokolle und Stände. */
export function datumUndUhr(wert: unknown): string {
  const tag = tagLang(wert);
  const uhr = uhrImBetrieb(wert);
  return uhr ? `${tag}, ${uhr}` : tag;
}

/**
 * Eine Wanduhr in Berlin (`JJJJ-MM-TT`, `HH:MM`) als Zeitpunkt — der Weg
 * zurück, für Eingaben, die Tag und Uhrzeit getrennt führen. In der
 * Umstellungsnacht gilt die erste passende Stunde.
 */
export function wandAlsZeitpunkt(tag: string, uhr: string): Date {
  const [j, m, t] = tag.split("-").map(Number);
  const [h, min] = uhr.split(":").map(Number);
  const alsUtc = Date.UTC(j ?? 1970, (m ?? 1) - 1, t ?? 1, h ?? 0, min ?? 0);
  let versuch = alsUtc;
  for (let i = 0; i < 2; i += 1) {
    const w = wandImBetrieb(new Date(versuch));
    if (!w) break;
    const gelesen = Date.UTC(w.jahr, w.monat - 1, w.tag, w.stunde, w.minute);
    versuch -= gelesen - alsUtc;
  }
  return new Date(versuch);
}
